import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { MutableRefObject } from 'react';

import {
	isBylineTitleDiagnosticsEnabled,
	measureBylineLayout,
	startBylineTitleDiagnostics,
	type BylineLayout,
} from './bylineTitleDiagnostics.ts';

// Static configs provide no subscription. Recheck on React renders, including config-off rollback.
const subscribeToStaticConfig = () => () => undefined;

export function useBylineTitleDiagnosticsEnabled(ufoName: string | undefined): boolean {
	const getSnapshot = useCallback(
		() => (ufoName ? isBylineTitleDiagnosticsEnabled(ufoName) : false),
		[ufoName],
	);
	return useSyncExternalStore(subscribeToStaticConfig, getSnapshot, getSnapshot);
}

export function useBylineTitleDiagnostics({
	appId,
	contentId,
	hasAuthoritativeSnapshot,
	manifestTitle,
	renderedTitle,
}: {
	appId: string;
	contentId: string;
	hasAuthoritativeSnapshot: boolean;
	manifestTitle: string;
	renderedTitle: string;
}): {
	triggerRef: MutableRefObject<HTMLElement | null>;
	start: () => void;
	resolve: (title?: string, failed?: boolean) => void;
} {
	const triggerRef = useRef<HTMLElement | null>(null);
	const attempt = useRef<ReturnType<typeof startBylineTitleDiagnostics>>(undefined);
	const pending = useRef<{ title: string; before?: BylineLayout } | undefined>(undefined);
	const mounted = useRef(false);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const start = useCallback(() => {
		if (hasAuthoritativeSnapshot && !attempt.current) {
			attempt.current = startBylineTitleDiagnostics(appId, contentId);
		}
	}, [appId, contentId, hasAuthoritativeSnapshot]);

	const resolve = useCallback(
		(title?: string, failed = false) => {
			if (!mounted.current || !attempt.current?.isActive()) {
				return;
			}
			const resolution = failed
				? 'failed'
				: title === undefined
					? 'missingTitle'
					: title === manifestTitle
						? 'matched'
						: 'mismatched';
			if (attempt.current.resolve(resolution) && title !== undefined) {
				// Called immediately before the existing title state update, while the manifest is visible.
				pending.current = { title, before: measureBylineLayout(triggerRef.current) };
			}
		},
		[manifestTitle],
	);

	useEffect(() => {
		const update = pending.current;
		if (!update || update.title !== renderedTitle) {
			return;
		}
		// Measure after React commits the new title. Never add a UFO hold or delay rendering for diagnostics.
		const frame = window.requestAnimationFrame(() => {
			if (attempt.current?.isActive()) {
				attempt.current.recordLayout(update.before, measureBylineLayout(triggerRef.current));
			}
			pending.current = undefined;
		});
		return () => window.cancelAnimationFrame(frame);
	}, [renderedTitle]);

	return { triggerRef, start, resolve };
}
